import { useMemo } from 'react'
import * as THREE from 'three'

interface PropsColarCervical {
  raioPescoco?: number
  alturaPescoco?: number
  escala?: number
}

/**
 * Colar Cervical Rígido de Resgate (Extrication Collar).
 *
 * Reproduz o colar cervical regulável de emergência:
 * - Estrutura de polietileno de alta densidade semirrígido.
 * - Apoio de mento (mentoneira) pré-moldado sob a mandíbula.
 * - Janela traqueal frontal ampla para visualização da traqueia e palpação de pulso carotídeo.
 * - Suporte occipital posterior e lingueta de fecho em velcro lateral.
 */
export function ColarCervical3D({
  raioPescoco = 0.065,
  alturaPescoco = 0.11,
  escala = 1,
}: PropsColarCervical) {
  const rExt = raioPescoco * 1.25
  const alt = alturaPescoco * 0.95

  const { shapeColar } = useMemo(() => {
    // Perfil curvado em U com abertura frontal para a janela traqueal
    const shape = new THREE.Shape()
    const r = rExt
    // Ângulos do aro (deixa abertura frontal para a janela)
    const anguloAbertura = Math.PI * 0.28
    const angInicio = Math.PI / 2 + anguloAbertura
    const angFim = Math.PI * 2.5 - anguloAbertura
    shape.absarc(0, 0, r, angInicio, angFim, false)
    shape.absarc(0, 0, r * 0.88, angFim, angInicio, true)
    return { shapeColar: shape }
  }, [rExt])

  return (
    <group scale={escala} position={[0, alturaPescoco * 0.48, 0]}>
      {/* Aro cervical principal */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -alt * 0.42, 0]}>
        <extrudeGeometry
          args={[
            shapeColar,
            { depth: alt * 0.85, bevelEnabled: true, bevelThickness: 0.004, bevelSize: 0.004, bevelSegments: 2 },
          ]}
        />
        <meshStandardMaterial color="#2a75d3" roughness={0.4} metalness={0.1} />
      </mesh>

      {/* Forro macio interno (espuma de conforto hipoalergênica) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -alt * 0.38, 0]}>
        <cylinderGeometry args={[rExt * 0.87, rExt * 0.87, alt * 0.78, 20, 1, true]} />
        <meshStandardMaterial color="#212529" roughness={0.9} side={THREE.DoubleSide} />
      </mesh>

      {/* Apoio mentoniano frontal (mentoneira) */}
      <group position={[0, alt * 0.32, rExt * 0.82]}>
        <mesh rotation={[Math.PI / 5, 0, 0]}>
          <boxGeometry args={[rExt * 1.1, 0.025, 0.045]} />
          <meshStandardMaterial color="#f0f4f8" roughness={0.5} />
        </mesh>
        {/* Janela traqueal vazada */}
        <mesh position={[0, -0.045, 0]}>
          <ringGeometry args={[0.016, 0.024, 16]} />
          <meshStandardMaterial color="#1a202c" side={THREE.DoubleSide} />
        </mesh>
      </group>

      {/* Fecho lateral de velcro (regulagem de altura / tamanho) */}
      <mesh position={[rExt * 0.98, -0.01, 0]} rotation={[0, Math.PI / 4, 0]}>
        <boxGeometry args={[0.012, alt * 0.5, 0.035]} />
        <meshStandardMaterial color="#e2e8f0" roughness={0.8} />
      </mesh>
    </group>
  )
}

